import { ErrorModal } from "@dexteel/mesf-core";
import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { TensileRequirementByGrade } from "@/pages/configuration/models/TensileRequirementByGrade";
import { MechanicalResultsTable } from "@/pages/mill/schedule/components/mechanical-results/MechanicalResultsTable";
import { useSaveMechanicalRun } from "@/pages/mill/schedule/components/mechanical-results/useSaveMechanicalRun";
import { CampaignResponse } from "@/pages/mill/schedule/models/CampaignReponse";
import { HeatMechanicalResult } from "@/pages/mill/schedule/models/HeatMechanicalResult";
import { MechanicalTestRunDisposition } from "@/pages/mill/schedule/models/MechanicalTestRunDisposition";
import {
	getHeatMechanicalResults,
	getMechanicalTestRunDispositions,
} from "@/pages/mill/schedule/repositories/CampaignRepository";
import { MILL_2, MillId } from "@/setup/mill-config/mill-config";

export const useHeatMechanicalResult = ({
	heatId,
}: {
	heatId?: number | null;
}) =>
	useQuery<
		{
			results: HeatMechanicalResult[];
			requirement: TensileRequirementByGrade | null;
		},
		Error
	>({
		queryKey: ["heatMechanicalResults", heatId],
		queryFn: ({ signal }) =>
			getHeatMechanicalResults({ heatId: heatId! }, signal),
		enabled: !!heatId,
	});

export const useHeatTestDisposition = ({
	heatId,
	millId = MILL_2.id,
}: {
	heatId?: number | null;
	millId?: MillId;
}) =>
	useQuery<MechanicalTestRunDisposition[], Error>({
		queryKey: ["heatTestDispositions", millId, heatId],
		queryFn: ({ signal }) =>
			heatId ? getMechanicalTestRunDispositions({ heatId }, signal) : [],
		enabled: !!heatId,
	});

type Props = {
	heat: CampaignResponse | null;
	millId?: MillId;
	canEdit?: boolean;
};

export const MechanicalResultsContent = ({
	heat,
	millId = MILL_2.id,
	canEdit = false,
}: Props) => {
	const [error, setError] = useState("");

	const { data, isLoading } = useHeatMechanicalResult({
		heatId: heat?.HeatId,
	});

	const results = data?.results;

	const { data: dispositions } = useHeatTestDisposition({
		heatId: heat?.HeatId,
		millId,
	});

	const { saveRun, isSaving } = useSaveMechanicalRun({
		heatId: heat?.HeatId,
		millId,
		onError: setError,
	});

	const hasAnything =
		(results?.length ?? 0) > 0 || (dispositions?.length ?? 0) > 0;

	const showTable = hasAnything || canEdit;

	return (
		<>
			{isLoading ? (
				<Box sx={{ py: 3 }}>
					<CenteredLazyLoading minHeight={50} />
				</Box>
			) : showTable ? (
				<MechanicalResultsTable
					results={results ?? []}
					requirement={data?.requirement ?? null}
					dispositions={dispositions ?? []}
					isSaving={isSaving}
					onSaveRun={canEdit ? saveRun : undefined}
					allowEmptyRun
				/>
			) : (
				<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
					No mechanical tests loaded for this heat.
				</Typography>
			)}

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
